/**
 * A key of {@link PushIterable} iteration method.
 */
const PushIterator__symbol = ( /*#__PURE__*/Symbol('push-iterator'));

function isPushIterable(iterable) {
    return !!iterable[PushIterator__symbol];
}

function iteratorOf(iterable) {
    return iterable[Symbol.iterator]();
}

/**
 * Creates a push iterable implementation.
 *
 * @typeParam T - Iterated elements type.
 * @param iterate - A function iterating over iterable elements conforming to {@link PushIterable.Iterate} requirements.
 *
 * @returns New push iterable instance performing iteration by `forNext` function.
 */
function makePushIterable(iterate) {
    return {
        [Symbol.iterator]: PushIterable$iterator,
        [PushIterator__symbol]: iterate,
    };
}
function PushIterable$iterator() {
    return this[PushIterator__symbol]();
}

function PushIterator$iterator() {
    return this;
}
function PushIterator$next() {
    for (;;) {
        let result;
        const tail = this[PushIterator__symbol](value => {
            result = { value };
            return true;
        }, 1 /* Next */);
        if (result) {
            return result;
        }
        if (tail.isOver()) {
            return { done: true };
        }
    }
}

const PushIterator$empty = {
    [Symbol.iterator]: PushIterator$iterator,
    [PushIterator__symbol](_accept, _mode) {
        return this;
    },
    next: PushIterator$noNext,
    isOver: PushIterator$over,
};
function PushIterator$noNext() {
    return { done: true };
}
function PushIterator$dontIterate(_accept, _mode) {
    // Do not iterate
}
function PushIterator$over() {
    return true;
}

/**
 * Creates a push iterator implementation.
 *
 * @typeParam T - Iterated elements type.
 * @param forNext - A function iterating over elements conforming to push iteration protocol.
 *
 * @returns New push iterator instance performing iteration by `forNext` function.
 */
function makePushIterator(forNext) {
    let over = false;
    let iterate = (accept) => {
        if (accept && !forNext(accept)) {
            over = true;
            iterate = PushIterator$dontIterate;
        }
    };
    return {
        [Symbol.iterator]: PushIterator$iterator,
        [PushIterator__symbol](accept) {
            iterate(accept);
            return this;
        },
        next: PushIterator$next,
        isOver: () => over,
    };
}

function indexed$itemOf(indexed, index) {
    return indexed.item(index); // The index is always valid.
}
function indexed$process(indexed, elementOf, accept, mode /* PushIterationMode.Only | PushIterationMode.All */) {
    if (mode === 2 /* All */) {
        for (let i = 0; i < indexed.length; ++i) {
            accept(elementOf(indexed, i));
        }
    }
    else {
        for (let i = 0; i < indexed.length; ++i) {
            if (accept(elementOf(indexed, i)) === false) {
                break;
            }
        }
    }
    return PushIterator$empty;
}
function indexed$some(indexed, elementOf, accept) {
    let i = 0;
    const forNext = (accept) => {
        if (i >= indexed.length) {
            return false;
        }
        for (;;) {
            const goOn = accept(elementOf(indexed, i++));
            if (i >= indexed.length || goOn === false) {
                return false;
            }
            if (goOn === true) {
                return true;
            }
        }
    };
    if (accept && !forNext(accept)) {
        return PushIterator$empty;
    }
    let over = false;
    let iterate = (accept) => {
        if (accept && !forNext(accept)) {
            over = true;
            iterate = PushIterator$dontIterate;
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            next = PushIterator$noNext;
        }
    };
    let next = () => {
        if (i < indexed.length) {
            return { value: elementOf(indexed, i++) };
        }
        over = true;
        iterate = PushIterator$dontIterate;
        next = PushIterator$noNext;
        return { done: true };
    };
    return {
        [Symbol.iterator]: PushIterator$iterator,
        [PushIterator__symbol](accept) {
            iterate(accept);
            return this;
        },
        next: () => next(),
        isOver: () => over,
    };
}
function indexed$iterate(indexed, elementOf) {
    return (accept, mode = 0 /* Some */) => accept && mode > 0
        ? indexed$process(indexed, elementOf, accept, mode)
        : indexed$some(indexed, elementOf, accept);
}

function arrayLike$elementOf(array, index) {
    return array[index];
}
function arrayLike$process(array, accept, mode /* PushIterationMode.Only | PushIterationMode.All */) {
    return indexed$process(array, arrayLike$elementOf, accept, mode);
}
function arrayLike$some(array, accept) {
    return indexed$some(array, arrayLike$elementOf, accept);
}
function arrayLike$iterate(array) {
    return indexed$iterate(array, arrayLike$elementOf);
}

/**
 * Creates a {@link PushIterable | push iterable} over elements of array-like structure.
 *
 * @typeParam T - Array elements type.
 * @param array - An array-like structure. E.g. `Array`, DOM `NodeList`, etc.
 *
 * @returns New push iterable over array elements.
 */
function overArray(array) {
    return makePushIterable(arrayLike$iterate(array));
}

function iterable$process(iterable, accept, mode /* PushIterationMode.Only | PushIterationMode.All */) {
    if (mode === 2 /* All */) {
        for (const element of iterable) {
            accept(element);
        }
    }
    else {
        for (const element of iterable) {
            if (accept(element) === false) {
                break;
            }
        }
    }
    return PushIterator$empty;
}

function iterator$convert(it, forNext) {
    let over = false;
    let iterate = (accept) => {
        if ((over = !!accept && !forNext(accept))) {
            iterate = PushIterator$dontIterate;
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            next = PushIterator$noNext;
        }
    };
    let next = () => {
        const res = it.next();
        if (res.done) {
            over = true;
            iterate = PushIterator$dontIterate;
            next = PushIterator$noNext;
        }
        return res;
    };
    return {
        [Symbol.iterator]: PushIterator$iterator,
        [PushIterator__symbol](accept) {
            iterate(accept);
            return this;
        },
        next() {
            return next();
        },
        isOver: () => over,
    };
}
function iterator$pusher(it) {
    return accept => {
        for (;;) {
            const res = it.next();
            if (res.done) {
                return false;
            }
            const status = accept(res.value);
            if (typeof status === 'boolean') {
                return status;
            }
        }
    };
}

/**
 * Iterates over elements of the given iterable.
 *
 * Calls `accept` method for each iterated element until there are elements to iterate, or `accept` returned either
 * `true` or `false`.
 *
 * @typeParam T - Iterated elements type.
 * @param iterable - An iterable to iterate elements of.
 * @param accept - A function to push iterated elements to. Accepts iterated element as its only parameter. May return
 * `true` to suspend iteration, or `false` to stop it.
 * @param mode - Optional iteration mode hint declaring what `accept` function shall do.
 *
 * @returns A push iterator instance representing the tail of the given iterable. This iterator can be used to continue
 * iteration with, unless `accept` returned `false`. In the latter case the further iteration won't be possible.
 */
function iterateIt(iterable, accept, mode = 0 /* Some */) {
    if (isPushIterable(iterable)) {
        return iterable[PushIterator__symbol](accept, mode);
    }
    if (Array.isArray(iterable)) {
        return iterateIt$array(iterable, accept, mode);
    }
    return iterateIt$raw(iterable, accept, mode);
}
function iterateIt$array(array, accept, mode) {
    return array.length
        ? (mode > 0 ? arrayLike$process(array, accept, mode) : arrayLike$some(array, accept))
        : PushIterator$empty;
}
function iterateIt$raw(iterable, accept, mode) {
    if (mode > 0) {
        return iterable$process(iterable, accept, mode);
    }
    const it = iterable[Symbol.iterator]();
    if (isPushIterable(it)) {
        return it[PushIterator__symbol](accept, mode);
    }
    const forEach = iterator$pusher(it);
    return forEach(accept)
        ? iterator$convert(it, forEach)
        : PushIterator$empty;
}

function flatMapIndexed$(indexed, elementsOf) {
    return (accept, mode = 0 /* Some */) => {
        if (accept && mode > 0) {
            return flatMapIndexed$process(indexed, elementsOf, accept, mode);
        }
        let i = 0;
        let subs;
        const forNext = (accept) => {
            if (i >= indexed.length) {
                return false;
            }
            if (!subs) {
                subs = elementsOf(indexed, i);
            }
            for (;;) {
                let status;
                const subsTail = iterateIt(subs, element => status = accept(element));
                if (subsTail.isOver()) {
                    if (++i >= indexed.length) {
                        return false;
                    }
                    subs = elementsOf(indexed, i);
                }
                else {
                    subs = subsTail;
                }
                if (typeof status === 'boolean') {
                    return status;
                }
            }
        };
        return accept && !forNext(accept)
            ? PushIterator$empty
            : makePushIterator(forNext);
    };
}
function flatMapIndexed$process(indexed, elementsOf, accept, mode /* PushIterationMode.Only | PushIterationMode.All */) {
    if (mode === 2 /* All */) {
        for (let i = 0; i < indexed.length; ++i) {
            iterateIt(elementsOf(indexed, i), accept, mode);
        }
    }
    else {
        let status;
        const subProcess = (element) => status = accept(element);
        for (let i = 0; i < indexed.length; ++i) {
            iterateIt(elementsOf(indexed, i), subProcess, mode);
            if (status === false) {
                break;
            }
        }
    }
    return PushIterator$empty;
}

function flatMapArray$(array, elementsOf) {
    return makePushIterable(flatMapIndexed$(array, elementsOf));
}
function flatMapArray$defaultElementOf(array, index) {
    return array[index];
}

/**
 * Creates a {@link PushIterable | push iterable} over elements of iterator created by the given function.
 *
 * @typeParam T - Iterated elements type.
 * @param iterate - A function creating new iterator.
 *
 * @returns New push iterable over elements of created iterator.
 */
function overIterator(iterate) {
    return makePushIterable(overIterator$iterate(iterate));
}
function overIterator$iterate(iterate) {
    return (accept, mode = 0 /* Some */) => {
        const it = iterate();
        if (isPushIterable(it)) {
            return it[PushIterator__symbol](accept, mode);
        }
        const forNext = iterator$pusher(it);
        return accept && !forNext(accept)
            ? PushIterator$empty
            : iterator$convert(it, forNext);
    };
}

/**
 * Creates a {@link PushIterable | push iterable} over elements of the given raw iterable.
 *
 * @typeParam T - Iterated elements type.
 * @param iterable - An iterable to iterate over elements of.
 *
 * @returns New push iterable over elements of the given `iterable`.
 */
function overIterable(iterable) {
    return Array.isArray(iterable)
        ? overArray(iterable)
        : overIterator(() => iterable[Symbol.iterator]());
}

/**
 * Creates a {@link PushIterable | push iterable} over elements of other iterables.
 *
 * @typeParam T - Iterated elements type.
 * @param sources - Source iterables to iterate over elements of.
 *
 * @returns New push iterable over elements of the given `sources`.
 */
function overElementsOf(...sources) {
    return sources.length > 1
        ? flatMapArray$(sources, flatMapArray$defaultElementOf)
        : (sources.length ? overIterable(sources[0]) : PushIterator$empty);
}

/**
 * Creates a {@link PushIterable | push iterable} over items of {@link IndexedItemList | indexed list}.
 *
 * @typeParam T - Indexed items type.
 * @param indexed - An indexed list of items. E.g. DOM `NodeList`.
 *
 * @returns New push iterable over list items.
 */
function overIndexed(indexed) {
    return makePushIterable(indexed$iterate(indexed, indexed$itemOf));
}

/**
 * Returns a {@link PushIterator push iterator} without elements.
 *
 * @typeParam T - Iterated elements type.
 *
 * @returns Empty push iterator instance.
 */
function overNone() {
    return PushIterator$empty;
}

/**
 * Creates a {@link PushIterable | push iterable} over one value.
 *
 * @typeParam T - Iterated element value type.
 * @param value - A value to iterate over.
 *
 * @returns New push iterable over the given value.
 */
function overOne(value) {
    return makePushIterable(iterateOverOneValue(value));
}
function iterateOverOneValue(value) {
    return accept => {
        if (accept) {
            accept(value);
            return PushIterator$empty;
        }
        let over = false;
        return {
            [Symbol.iterator]: PushIterator$iterator,
            [PushIterator__symbol](accept) {
                if (over) {
                    return PushIterator$empty;
                }
                if (accept) {
                    over = true;
                    accept(value);
                    return PushIterator$empty;
                }
                return this;
            },
            next() {
                if (over) {
                    return { done: over };
                }
                over = true;
                return { value };
            },
            isOver: () => over,
        };
    };
}

/**
 * Creates a {@link PushIterable | push iterable} over many values.
 *
 * @typeParam T - Iterated elements value type.
 * @param values - Values to iterate over.
 *
 * @returns New push iterable over the given values.
 */
function overMany(...values) {
    return values.length > 1
        ? overArray(values)
        : (values.length
            ? overOne(values[0])
            : overNone());
}

/**
 * Creates a {@link PushIterable | push iterable} over elements of array-like structure in reverse order.
 *
 * @typeParam T - Array elements type.
 * @param array - An array-like structure. E.g. `Array`, DOM `NodeList`, etc.
 *
 * @returns New push iterable over array elements in reverse order.
 */
function reverseArray(array) {
    return makePushIterable(reverseArray$iterate(array));
}
function reverseArray$iterate(array) {
    return (accept, mode = 0 /* Some */) => {
        if (accept && mode > 0) {
            return reverseArray$process(array, accept, mode);
        }
        let i = array.length - 1;
        const forNext = (accept) => {
            if (i < 0) {
                return false;
            }
            for (;;) {
                const status = accept(array[i--]);
                if (i < 0) {
                    return false;
                }
                if (typeof status === 'boolean') {
                    return true;
                }
            }
        };
        if (accept && !forNext(accept)) {
            return PushIterator$empty;
        }
        let over = false;
        let iterate = (accept) => {
            if (accept && !forNext(accept)) {
                over = true;
                iterate = PushIterator$dontIterate;
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                next = PushIterator$noNext;
            }
        };
        let next = () => {
            if (i < 0) {
                over = true;
                iterate = PushIterator$dontIterate;
                next = PushIterator$noNext;
                return { done: true };
            }
            return { value: array[i--] };
        };
        return {
            [Symbol.iterator]: PushIterator$iterator,
            [PushIterator__symbol](accept) {
                iterate(accept);
                return this;
            },
            next: () => next(),
            isOver: () => over,
        };
    };
}
function reverseArray$process(array, accept, mode /* PushIterationMode.Only | PushIterationMode.All */) {
    if (mode === 2 /* All */) {
        for (let i = array.length - 1; i >= 0; --i) {
            accept(array[i]);
        }
    }
    else {
        for (let i = array.length - 1; i >= 0; --i) {
            if (accept(array[i]) === false) {
                break;
            }
        }
    }
    return PushIterator$empty;
}

/**
 * Performs the given `action` for each element of the given `iterable`.
 *
 * @typeParam T - Iterated elements type.
 * @param iterable - An iterable of elements to perform actions on.
 * @param action - An action to perform on each iterable element. This is a function accepting an element as its only
 * parameter.
 */
function itsEach(iterable, action) {
    iterateIt(iterable, element => { action(element); }, 2 /* All */);
}

const itsElements$defaultConverter = (element) => element;
function itsElements(source, convert) {
    if (isPushIterable(source)) {
        return itsElements$(source, convert);
    }
    const it = source[Symbol.iterator]();
    return isPushIterable(it)
        ? itsElements$(it, convert)
        : convert
            ? Array.from(source, convert)
            : [...source];
}
function itsElements$(it, convert = itsElements$defaultConverter) {
    const result = [];
    it[PushIterator__symbol](element => { result.push(convert(element)); }, 2 /* All */);
    return result;
}

/**
 * Checks whether the given `iterable` is empty.
 *
 * @param iterable - An iterable or push iterable to check for elements.
 *
 * @returns `true` if the given iterable contains at least one element, or `false` otherwise.
 */
function itsEmpty(iterable) {
    if (isPushIterable(iterable)) {
        return itsEmpty$(iterable);
    }
    const it = iterable[Symbol.iterator]();
    return isPushIterable(it)
        ? itsEmpty$(it)
        : !!it.next().done;
}
function itsEmpty$(it) {
    let isEmpty = true;
    it[PushIterator__symbol](_element => isEmpty = false, 1 /* Only */);
    return isEmpty;
}

/**
 * Tests whether all elements of the given `iterable` pass the test implemented by the provided function.
 *
 * @typeParam T - Iterated elements type.
 * @param iterable - An iterable to test elements of.
 * @param test - A predicate function to test each element. Returns `true` to continue tests, or `false` to stop it
 * and return `false` from the method call. It accepts the tested element as the only parameter.
 *
 * @returns `true` if the `test` function returned a truthy value for every element, or `false` otherwise.
 * Returns `true` for empty iterable.
 */
function itsEvery(iterable, test) {
    let allMatch = true;
    iterateIt(iterable, (element) => {
        allMatch = !!test(element);
        return allMatch ? void 0 : false;
    }, 1 /* Only */);
    return allMatch;
}

/**
 * Searches for the value in `iterable`.
 *
 * @typeParam T - Iterated elements type.
 * @typeParam TFound - Found value type.
 * @param iterable - An iterable to extract element from.
 * @param search - A function extracting the value from elements. It is called for each iterated element until the value
 * found. Accepts element as the only parameter, and returns extracted value. If returns `false` or `undefined` the
 * search continues from the next element.
 *
 * @returns Either found value or `undefined`.
 */
function itsFind(iterable, search) {
    let find;
    iterateIt(iterable, (element) => {
        const result = search(element);
        if (result !== false && result !== undefined) {
            find = result;
            return false;
        }
    }, 1 /* Only */);
    return find;
}

/**
 * Extracts the first element of the given `iterable`, if any.
 *
 * @typeParam T - Iterated elements type.
 * @param iterable - An iterable to extract element from.
 *
 * @returns Either the first element, or `undefined` if the given `iterable` is empty.
 */
function itsFirst(iterable) {
    if (isPushIterable(iterable)) {
        return itsFirst$(iterable);
    }
    const it = iterable[Symbol.iterator]();
    return isPushIterable(it) ? itsFirst$(it) : itsFirst$raw(it);
}
function itsFirst$(it) {
    let first;
    it[PushIterator__symbol](element => {
        first = element;
        return false;
    }, 1 /* Only */);
    return first;
}
function itsFirst$raw(it) {
    const result = it.next();
    return result.done ? undefined : result.value;
}

/**
 * Starts iteration over the given `iterable`.
 *
 * @typeParam T - Iterated elements type.
 * @param iterable - An iterable or push iterable to iterate over.
 *
 * @returns A push iterator iterating over the given iterable.
 */
function itsIterator(iterable) {
    const it = iterable[Symbol.iterator]();
    return isPushIterable(it)
        ? it
        : iterator$convert(it, iterator$pusher(it));
}

/**
 * Extracts the first element matching the given condition from `iterable`.
 *
 * @typeParam T - Iterated elements type.
 * @param iterable - An iterable to extract element from.
 * @param test - A predicate function to test elements. Returns truthy value for matching one. It accepts the tested
 * element as the only parameter.
 *
 * @returns Either the matching element, or `undefined` if no elements match.
 */
function itsMatch(iterable, test) {
    let match;
    iterateIt(iterable, (element) => {
        if (test(element)) {
            match = element;
            return false;
        }
    }, 1 /* Only */);
    return match;
}

/**
 * Applies a function against an accumulator and each element of the given `iterable` to reduce elements to a single
 * value.
 *
 * @typeParam T - Iterated elements type.
 * @typeParam TResult - A type of reduced value.
 * @param iterable - An iterable to reduce values of.
 * @param reducer - A function to apply the value returned from the previous `reducer` call and to each element.
 * @param initialValue - Initial value passed to the first `reducer` call.
 *
 * @returns Reduced value returned from the last `reducer` call, or `initialValue` if there is no elements in the given
 * `iterable`.
 */
function itsReduction(iterable, reducer, initialValue) {
    let reduced = initialValue;
    iterateIt(iterable, element => { reduced = reducer(reduced, element); }, 2 /* All */);
    return reduced;
}

/**
 * Tests whether at least one element of the given `iterable` passes the test implemented by the provided function.
 *
 * @typeParam T - Iterated elements type.
 * @param iterable - An iterable to test elements of.
 * @param test - A predicate function to test each element. Returns `false` to continue tests, or `true` to stop it
 * and return `true` from the method call. It accepts the tested element as the only parameter.
 *
 * @returns `true` if the callback function returned a truthy value for at least one element in the array, or `false`
 * otherwise. Returns `false` for empty iterable.
 */
function itsSome(iterable, test) {
    let someMatches = false;
    iterateIt(iterable, (element) => {
        someMatches = !!test(element);
        return someMatches ? false : void 0;
    }, 1 /* Only */);
    return someMatches;
}

/**
 * Creates a {@link PushIterable | push iterable} over keys of the given object.
 *
 * A list of keys is constructed using `Reflect.ownKeys()`.
 *
 * @typeParam TObj - Source object type.
 * @param source - An object to select keys from.
 *
 * @returns New push iterable over own object keys retrieved by `Reflect.ownKeys()`.
 */
function overKeys(source) {
    return overArray(Reflect.ownKeys(source));
}

function filterIndexed$(indexed, elementOf, test) {
    return (accept, mode = 0 /* Some */) => {
        if (accept && mode > 0) {
            return indexed$process(indexed, elementOf, element => test(element) ? accept(element) : void 0, mode);
        }
        let i = 0;
        const forNext = (accept) => {
            for (;;) {
                if (i >= indexed.length) {
                    return false;
                }
                const value = elementOf(indexed, i++);
                if (test(value)) {
                    const status = accept(value);
                    if (typeof status === 'boolean') {
                        return status;
                    }
                }
            }
        };
        if (accept && !forNext(accept)) {
            return PushIterator$empty;
        }
        let over = false;
        let iterate = (accept) => {
            if (accept && !forNext(accept)) {
                over = true;
                iterate = PushIterator$dontIterate;
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                next = PushIterator$noNext;
            }
        };
        let next = () => {
            for (;;) {
                if (i >= indexed.length) {
                    over = true;
                    iterate = PushIterator$dontIterate;
                    next = PushIterator$noNext;
                    return { done: true };
                }
                const value = elementOf(indexed, i++);
                if (test(value)) {
                    return { value };
                }
            }
        };
        return {
            [Symbol.iterator]: PushIterator$iterator,
            [PushIterator__symbol](accept) {
                iterate(accept);
                return this;
            },
            next: () => next(),
            isOver: () => over,
        };
    };
}

function filterArray(array, test) {
    return makePushIterable(filterIndexed$(array, arrayLike$elementOf, test));
}

function filterIndexed(indexed, test) {
    return makePushIterable(filterIndexed$(indexed, indexed$itemOf, test));
}

function filterIt(source, test) {
    return makePushIterable((accept, mode = 0 /* Some */) => {
        if (accept && mode > 0) {
            const acceptElement = (element) => test(element) ? accept(element) : void 0;
            return isPushIterable(source)
                ? source[PushIterator__symbol](acceptElement, mode) // Iteration over.
                : iterable$process(source, acceptElement, mode);
        }
        const forNext = isPushIterable(source)
            ? filterIt$(source, test)
            : filterIt$raw(source, test);
        return accept && !forNext(accept)
            ? PushIterator$empty
            : makePushIterator(forNext);
    });
}
function filterIt$(source, test) {
    return accept => !(source = source[PushIterator__symbol](element => test(element) ? accept(element) : void 0)).isOver();
}
function filterIt$raw(source, test) {
    const it = source[Symbol.iterator]();
    if (isPushIterable(it)) {
        return filterIt$(it, test);
    }
    return accept => {
        for (;;) {
            const next = it.next();
            if (next.done) {
                return false;
            }
            const value = next.value;
            if (test(value)) {
                const status = accept(value);
                if (typeof status === 'boolean') {
                    return status;
                }
            }
        }
    };
}

function flatMapArray(array, convert) {
    return flatMapArray$(array, convert
        ? (array, index) => convert(array[index])
        : flatMapArray$defaultElementOf);
}

function flatMapIndexed(indexed, convert) {
    return makePushIterable(flatMapIndexed$(indexed, convert
        ? (indexed, index) => convert(indexed.item(index))
        : flatMapIndexed$defaultElementOf));
}
function flatMapIndexed$defaultElementOf(indexed, index) {
    return indexed.item(index);
}

function flatMapIt(source, convert = flatMapIt$defaultConverter) {
    return makePushIterable((accept, mode = 0 /* Some */) => {
        if (accept && mode > 0) {
            return isPushIterable(source)
                ? flatMapIt$process(source, convert, accept, mode)
                : flatMapIt$raw$process(source, convert, accept, mode);
        }
        const forNext = isPushIterable(source)
            ? flatMapIt$(source, convert)
            : flatMapIt$raw(source, convert);
        return accept && !forNext(accept)
            ? overNone()
            : makePushIterator(forNext);
    });
}
function flatMapIt$(source, convert) {
    let subs;
    let lastSrc = false;
    return accept => {
        for (;;) {
            while (!subs) {
                const sourceTail = source[PushIterator__symbol](src => {
                    subs = convert(src);
                    return true;
                });
                source = sourceTail;
                if (sourceTail.isOver()) {
                    if (!subs) {
                        return false;
                    }
                    lastSrc = true;
                }
            }
            let status;
            const subsTail = iterateIt(subs, element => status = accept(element));
            if (subsTail.isOver()) {
                subs = undefined;
                if (lastSrc) {
                    return false;
                }
            }
            else {
                subs = subsTail;
            }
            if (typeof status === 'boolean') {
                return status;
            }
        }
    };
}
function flatMapIt$raw(source, convert) {
    const it = source[Symbol.iterator]();
    if (isPushIterable(it)) {
        return flatMapIt$(it, convert);
    }
    let subs;
    return accept => {
        for (;;) {
            if (!subs) {
                const next = it.next();
                if (next.done) {
                    return false;
                }
                subs = convert(next.value);
            }
            // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
            let status;
            const subsTail = iterateIt(subs, element => status = accept(element));
            subs = subsTail.isOver() ? undefined : subsTail;
            if (typeof status === 'boolean') {
                return status;
            }
        }
    };
}
function flatMapIt$process(source, convert, accept, mode) {
    if (mode === 2 /* All */) {
        source[PushIterator__symbol](src => iterateIt(convert(src), accept, mode));
    }
    else {
        let status;
        const subProcess = (element) => status = accept(element);
        source[PushIterator__symbol]((src) => {
            iterateIt(convert(src), subProcess, mode);
            if (status === false) {
                return false;
            }
        });
    }
    return PushIterator$empty;
}
function flatMapIt$raw$process(source, convert, accept, mode) {
    if (mode === 2 /* All */) {
        for (const src of source) {
            iterateIt(convert(src), accept, mode);
        }
    }
    else {
        let status;
        const subProcess = (element) => status = accept(element);
        for (const src of source) {
            iterateIt(convert(src), subProcess, mode);
            if (status === false) {
                break;
            }
        }
    }
    return PushIterator$empty;
}
function flatMapIt$defaultConverter(element) {
    return element;
}

/**
 * Creates a {@link PushIterable | push iterable} with the results of calling a provided function on every element
 * of the given `array`.
 *
 * @typeParam TSrc - A type of array elements.
 * @typeParam TConv - A type of converted elements.
 * @param array - A source array-like instance.
 * @param convert - A function that produces an element of new iterable, taking array element as the only parameter.
 *
 * @returns New push iterable of transformed elements.
 */
function mapArray(array, convert) {
    return makePushIterable(indexed$iterate(array, (array, index) => convert(array[index])));
}

/**
 * Creates a {@link PushIterable | push iterable} with the results of calling a provided function on every item of the
 * given indexed list.
 *
 * @typeParam TSrc - A type of indexed list items.
 * @typeParam TConv - A type of converted elements.
 * @param indexed - A source indexed items list.
 * @param convert - A function that produces an element of new iterable, taking list item as the only parameter.
 *
 * @returns New push iterable of transformed elements.
 */
function mapIndexed(indexed, convert) {
    return makePushIterable(indexed$iterate(indexed, (list, index) => convert(list.item(index) /* The index is always valid */)));
}

/**
 * Creates a {@link PushIterable | push iterable} with the results of calling a provided function on every element
 * of the `source` iterable.
 *
 * @typeParam TSrc - A type of source elements.
 * @typeParam TConv - A type of converted elements.
 * @param source - A source iterable.
 * @param convert - A function that produces an element of the new iterable, taking the source element as the only
 * parameter.
 *
 * @returns New push iterable of transformed elements.
 */
function mapIt(source, convert) {
    return makePushIterable((accept, mode = 0 /* Some */) => {
        if (accept && mode > 0) {
            const acceptElement = (element) => accept(convert(element));
            return isPushIterable(source)
                ? source[PushIterator__symbol](acceptElement, mode) // Iteration over.
                : iterable$process(source, acceptElement, mode);
        }
        const forNext = isPushIterable(source)
            ? mapIt$(source, convert)
            : mapIt$raw(source, convert);
        return accept && !forNext(accept)
            ? overNone()
            : makePushIterator(forNext);
    });
}
function mapIt$(source, convert) {
    return accept => !(source = source[PushIterator__symbol](element => accept(convert(element)))).isOver();
}
function mapIt$raw(source, convert) {
    const it = source[Symbol.iterator]();
    if (isPushIterable(it)) {
        return mapIt$(it, convert);
    }
    return accept => {
        for (;;) {
            const next = it.next();
            if (next.done) {
                return false;
            }
            const status = accept(convert(next.value));
            if (typeof status === 'boolean') {
                return status;
            }
        }
    };
}

function valueIndexed$(indexed, elementOf, valueOf) {
    return (accept, mode = 0 /* Some */) => {
        if (accept && mode > 0) {
            return indexed$process(indexed, (source, index) => valueOf(elementOf(source, index)), value => value != null && value !== false ? accept(value) : void 0, mode);
        }
        let i = 0;
        const forNext = (accept) => {
            for (;;) {
                if (i >= indexed.length) {
                    return false;
                }
                const value = valueOf(elementOf(indexed, i++));
                if (value != null && value !== false) {
                    const status = accept(value);
                    if (typeof status === 'boolean') {
                        return status;
                    }
                }
            }
        };
        if (accept && !forNext(accept)) {
            return PushIterator$empty;
        }
        let over = false;
        let iterate = (accept) => {
            if (accept && !forNext(accept)) {
                over = true;
                iterate = PushIterator$dontIterate;
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                next = PushIterator$noNext;
            }
        };
        let next = () => {
            for (;;) {
                if (i >= indexed.length) {
                    over = true;
                    iterate = PushIterator$dontIterate;
                    next = PushIterator$noNext;
                    return { done: true };
                }
                const value = valueOf(elementOf(indexed, i++));
                if (value != null && value !== false) {
                    return { value };
                }
            }
        };
        return {
            [Symbol.iterator]: PushIterator$iterator,
            [PushIterator__symbol](accept) {
                iterate(accept);
                return this;
            },
            next: () => next(),
            isOver: () => over,
        };
    };
}

/**
 * Creates a {@link PushIterable | push iterable} with the values of elements of the given `array`.
 *
 * Element value is the result of provided function call, except `false`, `null`, and `undefined` which are filtered
 * out.
 *
 * This can be used as a more effective {@link mapArray} / {@link filterIt} combination.
 *
 * @typeParam T - A type of array elements.
 * @typeParam TValue - A type of array element values.
 * @param array - A source array.
 * @param valueOf - A function that values elements, taking the source element as the only parameter, and returning
 * either its value, or `false`/`null`/`undefined` to filter it out.
 *
 * @returns New push iterable with array element values.
 */
function valueArray(array, valueOf) {
    return makePushIterable(valueIndexed$(array, arrayLike$elementOf, valueOf));
}

/**
 * Creates a {@link PushIterable | push iterable} with the values of items of the given indexed list.
 *
 * Item value is the result of provided function call, except `false`, `null`, and `undefined` which are filtered out.
 *
 * This can be used as a more effective {@link mapIndexed} / {@link filterIt} combination.
 *
 * @typeParam T - Indexed items type.
 * @typeParam TValue - A type of item values.
 * @param indexed - A source indexed items list.
 * @param valueOf - A function that values items, taking the source item as the only parameter, and returning either
 * its value, or `false`/`null`/`undefined` to filter it out.
 *
 * @returns New push iterable with the item values.
 */
function valueIndexed(indexed, valueOf) {
    return makePushIterable(valueIndexed$(indexed, indexed$itemOf, valueOf));
}

/**
 * Creates a {@link PushIterable | push iterable} with the values of elements of the `source` iterable.
 *
 * Element value is the result of provided function call, except `false`, `null`, and `undefined` which are filtered
 * out.
 *
 * This can be used as a more effective {@link mapIt} / {@link filterIt} combination.
 *
 * @typeParam T - A type of source elements.
 * @typeParam TValue - A type of source element values.
 * @param source - A source iterable.
 * @param valueOf - A function that values elements, taking the source element as the only parameter, and returning
 * either its value, or `false`/`null`/`undefined` to filter it out.
 *
 * @returns New push iterable with the element values.
 */
function valueIt(source, valueOf) {
    return makePushIterable((accept, mode = 0 /* Some */) => {
        if (accept && mode > 0) {
            const acceptElement = (element) => {
                const value = valueOf(element);
                return value != null && value !== false
                    ? accept(value)
                    : void 0;
            };
            return isPushIterable(source)
                ? source[PushIterator__symbol](acceptElement, mode) // Iteration over.
                : iterable$process(source, acceptElement, mode);
        }
        const forNext = isPushIterable(source)
            ? valueIt$(source, valueOf)
            : valueIt$raw(source, valueOf);
        return accept && !forNext(accept)
            ? PushIterator$empty
            : makePushIterator(forNext);
    });
}
function valueIt$(source, valueOf) {
    return accept => !(source = source[PushIterator__symbol](element => {
        const value = valueOf(element);
        return value != null && value !== false
            ? accept(value)
            : void 0;
    })).isOver();
}
function valueIt$raw(source, valueOf) {
    const it = source[Symbol.iterator]();
    if (isPushIterable(it)) {
        return valueIt$(it, valueOf);
    }
    return accept => {
        for (;;) {
            const next = it.next();
            if (next.done) {
                return false;
            }
            const value = valueOf(next.value);
            if (value != null && value !== false) {
                const status = accept(value);
                if (typeof status === 'boolean') {
                    return status;
                }
            }
        }
    };
}

/**
 * Creates a {@link PushIterable | push iterable} over the property key/value entries of the given object.
 *
 * A list of keys is constructed using `Reflect.ownKeys()`.
 *
 * @typeParam TObj - Object type.
 *
 * @param source - An object to select keys and values from.
 *
 * @returns New push iterable of object property entries.
 */
function overEntries(source) {
    return mapIt(overKeys(source), key => [key, source[key]]);
}

export { PushIterator__symbol, filterArray, filterIndexed, filterIt, flatMapArray, flatMapIndexed, flatMapIt, isPushIterable, iteratorOf, itsEach, itsElements, itsEmpty, itsEvery, itsFind, itsFirst, itsIterator, itsMatch, itsReduction, itsSome, makePushIterable, makePushIterator, mapArray, mapIndexed, mapIt, overArray, overElementsOf, overEntries, overIndexed, overIterable, overIterator, overKeys, overMany, overNone, overOne, reverseArray, valueArray, valueIndexed, valueIt };
//# sourceMappingURL=push-iterator.js.map
